import { ChangeDetectionStrategy, Component, computed, inject, input, output } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Selectable option card (radio-style) with an icon slot, title and description.
 * Project the icon via `<span icon>…</span>`. Parent owns selection.
 */
@Component({
  selector: 'bb-option-card',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <button
      type="button"
      (click)="selected.emit()"
      class="flex w-full items-start gap-3 rounded-lg border p-4 text-left transition-all"
      [class]="active()
        ? 'border-brand-studio bg-brand-studio/10 ring-2 ring-brand-studio/25'
        : 'border-border bg-card hover:border-foreground/30'">
      <span
        class="grid size-10 flex-shrink-0 place-items-center rounded-md transition-colors [&_svg]:size-5"
        [class]="active() ? 'bg-brand-studio text-white' : 'bg-muted text-muted-foreground'">
        @if (iconHtml()) {
          <span [innerHTML]="trustedIcon()"></span>
        } @else {
          <ng-content select="[icon]" />
        }
      </span>
      <span class="min-w-0">
        <span class="block font-heading text-sm font-bold leading-tight text-foreground">{{ title() }}</span>
        @if (description()) {
          <span class="mt-1 block text-sm leading-snug text-muted-foreground">{{ description() }}</span>
        }
      </span>
    </button>
  `,
})
export class BbOptionCard {
  private readonly sanitizer = inject(DomSanitizer);

  readonly title = input<string>('');
  readonly description = input<string>('');
  readonly active = input<boolean>(false);
  /** Trusted, app-owned SVG markup for the icon (e.g. goal/format icons from layout config). */
  readonly iconHtml = input<string>('');
  readonly selected = output<void>();

  readonly trustedIcon = computed<SafeHtml>(() => this.sanitizer.bypassSecurityTrustHtml(this.iconHtml()));
}
